// Admin › Settings › Referral Program › Referrals and Ledger tabs (rewards part 4).
import type { ReferralAction, ReferralStage } from '../../api/referralRewards'
import type { LocalizedDict } from '../index'

export type RewardsReferralsStrings = {
  title: string
  subtitle: string
  loading: string
  loadError: string
  retry: string
  empty: string
  columns: { seller: string; referrer: string; stage: string; progress: string; rewards: string; actions: string }
  stages: Record<ReferralStage, string>
  feePaid: string
  feeMissing: string
  feeRefunded: string
  days: (n: number, min: number) => string
  orders: (n: number, min: number) => string
  actions: Record<ReferralAction, string>
  dialogTitle: Record<ReferralAction, (seller: string) => string>
  dialogBody: Record<ReferralAction, string>
  reasonLabel: string
  reasonPlaceholder: string
  reasonRequired: string
  cancel: string
  saving: string
}

export type RewardsLedgerStrings = {
  title: string
  subtitle: string
  loading: string
  loadError: string
  retry: string
  empty: string
  columns: { date: string; seller: string; movement: string; amount: string; expires: string; left: string }
  kinds: { granted: string; taken_back: string }
  sources: Record<string, string>
  never: string
}

export const rewardsReferralsI18n: LocalizedDict<RewardsReferralsStrings> = {
  en: {
    title: 'Rewards referrals',
    subtitle: 'Sellers brought in under the rewards program. A referral counts once the fee is paid, the days have passed and enough orders shipped; it is checked every night.',
    loading: 'Loading…',
    loadError: "Couldn't load the referrals.",
    retry: 'Try again',
    empty: 'No one has joined with a rewards code yet.',
    columns: { seller: 'Seller', referrer: 'Brought in by', stage: 'Status', progress: 'Progress', rewards: 'Rewards', actions: 'Actions' },
    stages: { signed_up: 'Signed up', activated: 'Joined', qualified: 'Counts', rejected: 'Rejected', reversed: 'Taken back' },
    feePaid: 'Fee paid',
    feeMissing: 'Fee not paid',
    feeRefunded: 'Fee refunded',
    days: (n, min) => `${Math.min(n, min)}/${min} days`,
    orders: (n, min) => `${Math.min(n, min)}/${min} orders`,
    actions: { qualify: 'Count it', reject: 'Reject', reverse: 'Take back' },
    dialogTitle: {
      qualify: (seller) => `Count ${seller} now?`,
      reject: (seller) => `Reject ${seller}?`,
      reverse: (seller) => `Take back ${seller}?`,
    },
    dialogBody: {
      qualify: 'It counts without waiting for the rules, and the reward it earns is paid right away.',
      reject: 'It will never count. This cannot be undone.',
      reverse: 'It stops counting. Rewards it no longer earns are removed — only the unspent part.',
    },
    reasonLabel: 'Reason',
    reasonPlaceholder: 'e.g. Same owner as an existing seller',
    reasonRequired: 'Write at least 3 characters.',
    cancel: 'Cancel',
    saving: 'Saving…',
  },
  tr: {
    title: 'Ödül programı referansları',
    subtitle: 'Ödül programıyla getirilen satıcılar. Ücret ödenip gün dolunca ve yeterli sipariş gönderilince referans sayılır; kontrol her gece yapılır.',
    loading: 'Yükleniyor…',
    loadError: 'Referanslar yüklenemedi.',
    retry: 'Tekrar dene',
    empty: 'Henüz ödül koduyla katılan yok.',
    columns: { seller: 'Satıcı', referrer: 'Getiren', stage: 'Durum', progress: 'İlerleme', rewards: 'Ödül', actions: 'İşlemler' },
    stages: { signed_up: 'Kaydoldu', activated: 'Katıldı', qualified: 'Sayıldı', rejected: 'Reddedildi', reversed: 'Geri alındı' },
    feePaid: 'Ücret ödendi',
    feeMissing: 'Ücret ödenmedi',
    feeRefunded: 'Ücret iade edildi',
    days: (n, min) => `${Math.min(n, min)}/${min} gün`,
    orders: (n, min) => `${Math.min(n, min)}/${min} sipariş`,
    actions: { qualify: 'Say', reject: 'Reddet', reverse: 'Geri al' },
    dialogTitle: {
      qualify: (seller) => `${seller} şimdi sayılsın mı?`,
      reject: (seller) => `${seller} reddedilsin mi?`,
      reverse: (seller) => `${seller} geri alınsın mı?`,
    },
    dialogBody: {
      qualify: 'Kuralları beklemeden sayılır; hak ettiği ödül hemen verilir.',
      reject: 'Hiçbir zaman sayılmaz. Geri alınamaz.',
      reverse: 'Artık sayılmaz. Hak edilmeyen ödüller geri alınır — yalnız harcanmamış kısım.',
    },
    reasonLabel: 'Gerekçe',
    reasonPlaceholder: 'ör. Mevcut bir satıcıyla aynı sahip',
    reasonRequired: 'En az 3 karakter yaz.',
    cancel: 'Vazgeç',
    saving: 'Kaydediliyor…',
  },
}

export const rewardsLedgerI18n: LocalizedDict<RewardsLedgerStrings> = {
  en: {
    title: 'Reward credit ledger',
    subtitle: 'The newest credit granted and taken back across all wallets.',
    loading: 'Loading…',
    loadError: "Couldn't load the ledger.",
    retry: 'Try again',
    empty: 'No reward credit has moved yet.',
    columns: { date: 'Date', seller: 'Seller', movement: 'Movement', amount: 'Amount', expires: 'Expires', left: 'Left' },
    kinds: { granted: 'Granted', taken_back: 'Taken back' },
    sources: {
      entry: 'Entry credit',
      threshold: 'Big reward',
      per_referral: 'Referral reward',
      commission: 'Commission',
      conversion_base: 'Converted',
      conversion_bonus: 'Conversion bonus',
      manual: 'Manual',
    },
    never: 'Never',
  },
  tr: {
    title: 'Ödül kredisi hareketleri',
    subtitle: 'Tüm cüzdanlarda en son verilen ve geri alınan krediler.',
    loading: 'Yükleniyor…',
    loadError: 'Hareketler yüklenemedi.',
    retry: 'Tekrar dene',
    empty: 'Henüz ödül kredisi hareketi yok.',
    columns: { date: 'Tarih', seller: 'Satıcı', movement: 'Hareket', amount: 'Tutar', expires: 'Süresi', left: 'Kalan' },
    kinds: { granted: 'Verildi', taken_back: 'Geri alındı' },
    sources: {
      entry: 'Giriş kredisi',
      threshold: 'Büyük ödül',
      per_referral: 'Referans ödülü',
      commission: 'Pay',
      conversion_base: 'Çevrilen',
      conversion_bonus: 'Çevirme bonusu',
      manual: 'Elle',
    },
    never: 'Süresiz',
  },
}
